import * as React from "react";

import { datadogRum } from "@datadog/browser-rum";

import { getErrorDetails } from "components/error-boundary/error-boundary.utils";
import ErrorDisplay from "components/error-boundary/error-display.component";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  sessionId?: string;
  errorDetails?: string;
}

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  routeName?: string;
}

export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      sessionId: undefined,
      errorDetails: undefined
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const context = datadogRum.getInternalContext();
    const sessionId = context?.session_id;
    const errorDetails = getErrorDetails(
      error,
      errorInfo,
      "RouteErrorBoundary"
    );

    // Update state with sessionId and errorDetails
    this.setState({
      sessionId,
      errorDetails
    });

    datadogRum.addError(error, {
      errorInfo,
      context: {
        triggeredBy: "RouteErrorBoundary",
        sessionId,
        errorDetails
      }
    });

    datadogRum.addAction("route_error_boundary_crash", {
      errorName: error.name,
      errorMessage: error.message,
      componentStack: errorInfo.componentStack,
      sessionId,
      errorDetails
    });

    console.error("Route loading error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          error={this.state.error}
          errorDetails={this.state.errorDetails}
          sessionId={this.state.sessionId}
          routeName={this.props.routeName}
          isCentered={false}
          showReloadButton={true}
        />
      );
    }

    return this.props.children;
  }
}
